<template>
  <div>
    <!-- Floating trigger button (positions next to freshworks-container) -->
    <button
      class="ai-chat-fab"
      :class="{ 'ai-chat-fab-active': isOpen }"
      title="AI Assistant"
      @click="toggle"
    >
      <i class="fa fa-comments fa-lg" />
    </button>

    <!-- Slide-out panel -->
    <transition name="ai-chat-slide">
      <div v-if="isOpen" class="ai-chat-panel">
        <!-- Header -->
        <div class="ai-chat-panel-header">
          <span class="ai-chat-panel-title">
            <i class="fa fa-comments mr-1" />AI Assistant
          </span>
          <div class="ai-chat-panel-header-actions">
            <button
              class="btn btn-sm btn-link text-white py-0"
              :disabled="isStreaming"
              title="Chat history"
              @click="toggleHistory"
            ><i class="fas fa-history" /></button>
            <button
              class="btn btn-sm btn-link text-white py-0"
              :disabled="isStreaming"
              title="Start new chat"
              @click="newSession"
            >New</button>
            <button
              class="btn btn-sm btn-link text-white py-0 ml-1"
              title="Close"
              @click="close"
            ><i class="fas fa-times" /></button>
          </div>
        </div>

        <!-- History view -->
        <div v-if="isHistoryOpen" class="ai-chat-panel-history">
          <div v-if="isLoadingHistory" class="ai-chat-panel-history-loading">
            <i class="fas fa-circle-notch fa-spin mr-2" />Loading history…
          </div>
          <div v-else-if="sessions.length === 0" class="ai-chat-panel-history-empty">
            No previous conversations found.
          </div>
          <div
            v-for="session in sessions"
            :key="session.id"
            class="ai-chat-panel-history-item"
          >
            <div class="ai-chat-panel-history-preview" @click="resumeSession({ sessionId: session.id })">
              <div class="ai-chat-panel-history-text">{{ session.previewText || (session.turnCount ? session.turnCount + ' message' + (session.turnCount !== 1 ? 's' : '') : 'Chat session') }}</div>
              <div class="ai-chat-panel-history-date">{{ formatSessionDate(session.lastActivityUtc || session.lastActivityAt) }}</div>
            </div>
            <button
              class="btn btn-sm btn-link text-muted ai-chat-panel-history-delete"
              title="Delete"
              @click="deleteSession({ sessionId: session.id })"
            ><i class="fas fa-times" /></button>
          </div>
        </div>

        <!-- Message thread -->
        <div v-if="!isHistoryOpen" class="ai-chat-panel-messages" ref="messagesEl">
          <!-- Empty state with suggested prompts -->
          <div v-if="turns.length === 0 && !isStreaming" class="ai-chat-panel-empty">
            <i class="fa fa-comments fa-2x mb-2" />
            <p class="mb-2">Ask me anything about your events.</p>
            <ul class="ai-chat-panel-suggestions">
              <li @click="fillPrompt('How many events occurred last month?')">
                How many events occurred last month?
              </li>
              <li @click="fillPrompt('Show me all high-severity events still open.')">
                Show me all high-severity events still open.
              </li>
              <li @click="fillPrompt('Which property had the most events this year?')">
                Which property had the most events this year?
              </li>
              <li @click="fillPrompt('What is the total insurance claim cost across all properties?')">
                What is the total insurance claim cost this year?
              </li>
            </ul>
          </div>

          <!-- Completed turns -->
          <div
            v-for="turn in turns"
            :key="turn.id"
            class="ai-chat-turn"
            :class="'ai-chat-turn-' + turn.role"
          >
            <div class="ai-chat-turn-content" v-html="renderText(turn.content)" />
          </div>

          <!-- Live streaming turn -->
          <div v-if="isStreaming" class="ai-chat-turn ai-chat-turn-assistant">
            <div v-if="streamingContent" class="ai-chat-turn-content">
              <span v-html="renderText(streamingContent)" /><span class="ai-chat-cursor">▋</span>
            </div>
            <div v-else class="ai-chat-turn-thinking">
              <b-spinner small class="mr-1" />Searching your data…
            </div>
          </div>
        </div>

        <!-- Error banner -->
        <div v-if="error && !isHistoryOpen" class="ai-chat-panel-error">
          <i class="fas fa-exclamation-triangle mr-1" />{{ error }}
        </div>

        <!-- Input area -->
        <div v-if="!isHistoryOpen" class="ai-chat-panel-input-area">
          <textarea
            id="chat-input"
            ref="inputEl"
            v-model="draft"
            class="ai-chat-panel-textarea form-control"
            placeholder="Ask about your events…"
            :disabled="isStreaming"
            rows="2"
            @keydown.enter.exact.prevent="submit"
          />
          <button
            class="btn btn-primary ai-chat-panel-send-btn"
            :disabled="isStreaming || !draft.trim()"
            @click="submit"
          >
            <i v-if="isStreaming" class="fas fa-circle-notch fa-spin" />
            <i v-else class="fas fa-paper-plane" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Backdrop (closes panel on click outside) -->
    <div v-if="isOpen" class="ai-chat-backdrop" @click="close" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { marked } from 'marked'

// Open links in a new tab and never inject target into other elements
const renderer = new marked.Renderer()
renderer.link = (href, title, text) =>
  `<a href="${href}" title="${title || ''}" target="_blank" rel="noopener noreferrer">${text}</a>`

marked.setOptions({ renderer, gfm: true, breaks: true })

export default {
  name: 'AiChatPanel',

  data () {
    return {
      draft: ''
    }
  },

  computed: {
    ...mapGetters('chatbot', ['isOpen', 'turns', 'isStreaming', 'streamingContent', 'error',
      'isHistoryOpen', 'sessions', 'isLoadingHistory'])
  },

  watch: {
    turns () { this.scrollToBottom() },
    streamingContent () { this.scrollToBottom() }
  },

  methods: {
    ...mapActions('chatbot', ['toggle', 'close', 'newSession', 'sendMessage',
      'toggleHistory', 'resumeSession', 'deleteSession']),

    async submit () {
      const message = this.draft.trim()
      if (!message || this.isStreaming) return
      this.draft = ''
      await this.sendMessage({ message })
    },

    fillPrompt (text) {
      this.draft = text
      this.$nextTick(() => this.$refs.inputEl && this.$refs.inputEl.focus())
    },

    formatSessionDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const now = new Date()
      const diffDays = Math.floor((now - d) / 86400000)
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      if (diffDays === 0) return `Today ${time}`
      if (diffDays === 1) return `Yesterday ${time}`
      if (diffDays < 7) return `${diffDays} days ago ${time}`
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + time
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messagesEl
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    /**
     * Render markdown from the AI response as safe HTML.
     * marked handles escaping; links open in a new tab.
     */
    renderText (text) {
      if (!text) return ''
      return marked.parse(text)
    }
  }
}
</script>

<style>
/* Floating trigger button */
.ai-chat-fab {
  position: fixed;
  bottom: 30px;
  right: 15px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgb(55, 163, 220);;
  color: #fff;
  border: 1px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9990;
  transition: background 0.2s, transform 0.2s;
}

@media screen and (max-width: 767px) {
  .ai-chat-fab {
    display: none;
  }
}

.ai-chat-fab:hover {
  background: rgb(32, 166, 238);;
  transform: scale(1.05);
}
.ai-chat-fab-active {
  background: #2563b0;
}

/* Backdrop */
.ai-chat-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9991;
}

/* Slide-out panel */
.ai-chat-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 40%;
  min-width: 400px;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: #1d3443;
  color: #d4d4d4;
  box-shadow: -4px 0 24px rgba(0,0,0,0.6);
  border-left: 1px solid rgba(255,255,255,0.5);
  z-index: 9992;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

/* Slide transition */
.ai-chat-slide-enter-active,
.ai-chat-slide-leave-active {
  transition: transform 0.25s ease;
}
.ai-chat-slide-enter,
.ai-chat-slide-leave-to {
  transform: translateX(100%);
}

/* Header */
.ai-chat-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #0c2032;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  flex-shrink: 0;
}
.ai-chat-panel-title {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
}
.ai-chat-panel-header-actions {
  display: flex;
  align-items: center;
}

/* Messages */
.ai-chat-panel-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Empty state */
.ai-chat-panel-empty {
  color: #efefef;
  font-size: 0.95rem;
  text-align: center;
  margin: auto;
}
.ai-chat-panel-suggestions {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  text-align: left;
}
.ai-chat-panel-suggestions li {
  padding: 6px 10px;
  margin: 4px 0;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.1);
  cursor: pointer;
  font-style: italic;
  color: rgb(55, 163, 220);
  transition: background 0.25s;
}
.ai-chat-panel-suggestions li:hover {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.2);
}

/* Chat turns */
.ai-chat-turn {
  max-width: 100%;
}
.ai-chat-turn p {
  margin: 0;
}
.ai-chat-turn-user .ai-chat-turn-content {
  background: rgba(255,255,255,0.8);
  border-radius: 8px 8px 2px 8px;
  padding: 8px 12px;
  margin-left: 32px;
  font-size: 0.88rem;
  color: #333333;
  word-break: break-word;
}
.ai-chat-turn-assistant .ai-chat-turn-content h2{
  font-size: 1.6em;
  font-weight: bold;
  margin-top: 1.5em;
  margin-bottom: 0.75em;
}
.ai-chat-turn-assistant .ai-chat-turn-content h3{
  font-size: 1.3em;
  font-weight: 900;
  margin-top: 1.5em;
  margin-bottom: 0.75em;
}
.ai-chat-turn-assistant .ai-chat-turn-content {
  color: #d4d4d4;
  font-size: 0.88rem;
  line-height: 1.6;
  word-break: break-word;
}
.ai-chat-turn-assistant .ai-chat-turn-content code {
  background: #2a2a45;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.85em;
}
.ai-chat-turn-assistant .ai-chat-turn-content pre {
  background: #2a2a45;
  padding: 10px 12px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 0.83rem;
  margin: 6px 0;
}
.ai-chat-turn-assistant .ai-chat-turn-content pre code {
  background: none;
  padding: 0;
}
.ai-chat-turn-assistant .ai-chat-turn-content ul,
.ai-chat-turn-assistant .ai-chat-turn-content ol {
  padding-left: 20px;
  margin: 4px 0;
}
.ai-chat-turn-assistant .ai-chat-turn-content p {
  margin: 4px 0;
}
.ai-chat-turn-assistant .ai-chat-turn-content a {
  color: #7db3e8;
}
.ai-chat-turn-thinking {
  color: #9090b0;
  font-size: 0.85rem;
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-chat-turn-assistant .ai-chat-turn-content table {
  width: 100%;
}

.ai-chat-turn-assistant .ai-chat-turn-content table thead th {
  padding: 1em;
  font-size: clamp(0.4rem, 1vw, 0.8rem);
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.7);
  word-break: keep-all;
}

.ai-chat-turn-assistant .ai-chat-turn-content table thead th:first-child {
  border-radius: 0.25em 0 0 0.25em;
}

.ai-chat-turn-assistant .ai-chat-turn-content table thead th:last-child {
  border-radius: 0 0.25em 0.25em 0;
}

.ai-chat-turn-assistant .ai-chat-turn-content table tbody tr td{
  padding: 0.5em 1em;
  font-size: clamp(0.5rem, 1vw, 0.9rem);
  width: fit-content;
  word-break:keep-all;
}

/* Blinking cursor */
.ai-chat-cursor {
  display: inline-block;
  animation: ai-chat-blink 1s step-end infinite;
  color: #7db3e8;
}
@keyframes ai-chat-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Error banner */
.ai-chat-panel-error {
  background: #4a1a1a;
  color: #f0a0a0;
  font-size: 0.82rem;
  padding: 8px 14px;
  flex-shrink: 0;
}

/* Input area */
.ai-chat-panel-input-area {
  display: flex;
  gap: 8px;
  padding: 0.75em 1em;
  border-top: 1px solid rgba(0,0,0,0.1);
  background: #0c2032;
  flex-shrink: 0;
  align-items: stretch;
}
.ai-chat-panel-textarea {
  flex: 1;
  resize: none;
  background: rgba(255,255,255,0.1) !important;
  color: #d4d4d4 !important;
  border-color: rgba(255,255,255,0.2) !important;
  font-size: 0.88rem !important;
}
.ai-chat-panel-textarea::placeholder {
  color: rgba(255,255,255,0.5);
}
.ai-chat-panel-textarea:focus {
  border-color: #569cd6 !important;
  box-shadow: 0 0 0 2px rgba(86, 156, 214, 0.25) !important;
}
.ai-chat-panel-send-btn {
  flex-shrink: 0;
  background: rgb(55, 163, 220);
  border-color: rgb(55, 163, 220);
}

/* History view */
.ai-chat-panel-history {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
}
.ai-chat-panel-history-loading,
.ai-chat-panel-history-empty {
  color: #9090b0;
  font-size: 0.85rem;
  text-align: center;
  padding: 24px 14px;
}
.ai-chat-panel-history-item {
  display: flex;
  align-items: center;
  padding: 0 8px 0 14px;
  border-bottom: 1px solid #2a2a45;
  transition: background 0.15s;
}
.ai-chat-panel-history-item:hover {
  background: #2a2a45;
}
.ai-chat-panel-history-preview {
  flex: 1;
  padding: 10px 0;
  cursor: pointer;
  min-width: 0;
}
.ai-chat-panel-history-text {
  font-size: 0.85rem;
  color: #d4d4d4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ai-chat-panel-history-date {
  font-size: 0.75rem;
  color: #6060a0;
  margin-top: 2px;
}
.ai-chat-panel-history-delete {
  flex-shrink: 0;
  opacity: 0.4;
  padding: 4px 6px;
}
.ai-chat-panel-history-item:hover .ai-chat-panel-history-delete {
  opacity: 1;
}

body #localize-widget.localize-right-bottom {
  right: 20% !important;
  z-index: 1050;
}
</style>



// WEBPACK FOOTER //
// src/components/AiChatPanel.vue